import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { RefreshCw, Loader2, PieChart as PieIcon, BarChart3 } from "lucide-react";

/**
 * Admin → Reports & Analytics
 *
 * Visual design locked-in by user (white-panel + icon header + uppercase
 * title + right-aligned count, donut with legend chips below, then a
 * METRIC | COUNT | % table). Eight panels in a responsive 4-col grid.
 *
 * Realtime: REFRESH_MS auto-refetch every panel.
 * Permission: gated by `admin_reports` (see backend reports_module).
 */

const REFRESH_MS = 15000;

const REPORTS = [
  { key: "campaign",      title: "LEADS BY CAMPAIGN",        icon: PieIcon,   metricLabel: "Campaign" },
  { key: "country",       title: "LEADS BY COUNTRY",         icon: BarChart3, metricLabel: "Country" },
  { key: "asset",         title: "LEADS BY ASSETS",          icon: PieIcon,   metricLabel: "Asset" },
  { key: "industry",      title: "LEADS BY INDUSTRY",        icon: BarChart3, metricLabel: "Industry" },
  { key: "function",      title: "LEADS BY FUNCTION / ROLE", icon: PieIcon,   metricLabel: "Function / Role" },
  { key: "job_title",     title: "LEADS BY JOB TITLE",       icon: BarChart3, metricLabel: "Job Title" },
  { key: "employee_size", title: "LEADS BY EMPLOYEE SIZE",   icon: PieIcon,   metricLabel: "Employee Size" },
  { key: "revenue_size",  title: "LEADS BY REVENUE SIZE",    icon: BarChart3, metricLabel: "Revenue Size" },
];

// Slice palette — high-contrast hues tuned for white panel background.
// Navy dominant + supporting cyan/blue/red/purple/green/orange spread.
const PALETTE = [
  "#0F2A4A", "#54B0E6", "#5B7EE8", "#E14B4B",
  "#7DBF5F", "#9E5BE0", "#F0A437", "#3FB4C2",
  "#D364C0", "#94A3B8",
];

export default function AdminReports() {
  const { options } = useAuth();
  const [campaign, setCampaign] = useState("");
  const [campaignsList, setCampaignsList] = useState([]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/campaigns");
        setCampaignsList(data.campaigns || []);
      } catch {
        setCampaignsList(options?.campaigns || []);
      }
    })();
  }, [options?.campaigns]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = (k) => ({ dimension: k, top: 30, ...(campaign ? { campaign_name: campaign } : {}) });
      const results = await Promise.all(
        REPORTS.map((r) => api.get("/reports/leads/breakdown", { params: params(r.key) })
          .then((res) => [r.key, res.data])
          .catch(() => [r.key, { items: [], total: 0 }])),
      );
      const m = {};
      results.forEach(([k, v]) => { m[k] = v; });
      setData(m);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [campaign]);
  useEffect(() => {
    const id = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [campaign]);

  return (
    <div className="space-y-4" data-testid="admin-reports">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold">Reports &amp; Analytics</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Live breakdowns over the leads database · auto-refreshes every {REFRESH_MS / 1000}s
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="reports-campaign-filter"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            className="h-9 px-3 border border-input bg-background text-sm rounded-sm min-w-[240px]"
          >
            <option value="">All Campaigns</option>
            {campaignsList.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={fetchAll}
            disabled={loading}
            data-testid="reports-refresh"
            className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50 h-9 px-3"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh now
          </button>
          {lastRefresh && (
            <span className="text-[11px] text-muted-foreground" data-testid="reports-last-refresh">
              Last: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* 4-column responsive grid of panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {REPORTS.map((r) => (
          <ReportPanel key={r.key} report={r} payload={data[r.key]} />
        ))}
      </div>

      {/* Leads by Agent — separate full-width panel because the two-tier
          breakdown (agent → country) doesn't fit in the 4-col grid */}
      <LeadsByAgentPanel campaign={campaign} refreshTick={lastRefresh?.getTime() || 0} />
    </div>
  );
}

function LeadsByAgentPanel({ campaign, refreshTick }) {
  const [payload, setPayload] = useState({ total: 0, agents: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // agent_id → bool

  const fetchIt = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/leads-by-agent", {
        params: campaign ? { campaign_name: campaign } : {},
      });
      setPayload(data);
    } catch {
      setPayload({ total: 0, agents: [] });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchIt(); /* eslint-disable-next-line */ }, [campaign, refreshTick]);

  const agents = payload.agents || [];

  return (
    <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="report-panel-leads-by-agent">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Leads by Agent · By Country</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground tabular-nums" data-testid="lba-total">
            {(payload.total || 0).toLocaleString()} total
          </span>
          <button
            onClick={fetchIt}
            disabled={loading}
            data-testid="lba-refresh"
            className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin inline" /> : <RefreshCw size={12} className="inline" />}
          </button>
        </div>
      </div>
      {agents.length === 0 ? (
        <div className="py-8 text-center text-xs text-muted-foreground italic">No leads recorded yet.</div>
      ) : (
        <div className="max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="lba-table">
            <thead className="bg-secondary sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">Agent</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">Email</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">Leads</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">Countries</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => {
                const key = a.agent_id || a.agent_email;
                const isOpen = !!expanded[key];
                const primaryCountry = (a.by_country || [])[0];
                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-border" data-testid={`lba-row-${key}`}>
                      <td className="px-3 py-1.5 font-medium">
                        <button
                          className="text-left hover:underline"
                          onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                          data-testid={`lba-expand-${key}`}
                        >
                          {isOpen ? "▼ " : "▶ "}{a.agent_name}
                        </button>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-muted-foreground">{a.agent_email}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{a.count.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-[10px] text-muted-foreground truncate max-w-[400px]">
                        {a.by_country?.length ? (
                          <>
                            <strong>{a.by_country.length}</strong>
                            {primaryCountry && ` · Top: ${primaryCountry.country} (${primaryCountry.count})`}
                          </>
                        ) : "—"}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-secondary/30">
                        <td colSpan={4} className="px-6 py-2">
                          <table className="w-full text-[11px]" data-testid={`lba-country-detail-${key}`}>
                            <thead>
                              <tr className="text-[9px] uppercase tracking-wider text-muted-foreground">
                                <th className="text-left py-1">Country</th>
                                <th className="text-right py-1">Count</th>
                                <th className="text-right py-1">% of agent</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(a.by_country || []).map((c) => (
                                <tr key={c.country} className="border-t border-border/60">
                                  <td className="py-1">{c.country}</td>
                                  <td className="text-right font-mono tabular-nums py-1">{c.count.toLocaleString()}</td>
                                  <td className="text-right font-mono tabular-nums text-muted-foreground py-1">
                                    {a.count ? ((c.count / a.count) * 100).toFixed(1) : "0"}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReportPanel({ report, payload }) {
  const items = payload?.items || [];
  const total = payload?.total || 0;

  // Top 7 slices in the chart; remainder bucketed as "Other" so the
  // donut stays legible even when a dim has dozens of long-tail values.
  const chartData = useMemo(() => {
    if (items.length <= 7) return items.map((x) => ({ name: x.key, value: x.count }));
    const head = items.slice(0, 6).map((x) => ({ name: x.key, value: x.count }));
    const tail = items.slice(6).reduce((s, x) => s + x.count, 0);
    return [...head, { name: "Other", value: tail }];
  }, [items]);

  const Icon = report.icon;

  return (
    <div className="border border-border rounded-sm bg-card overflow-hidden flex flex-col" data-testid={`report-panel-${report.key}`}>
      {/* Header — icon + uppercase title + right-aligned total count */}
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-primary" />
          <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground" data-testid={`report-title-${report.key}`}>
            {report.title}
          </span>
        </div>
        <span className="text-sm text-muted-foreground tabular-nums" data-testid={`report-total-${report.key}`}>
          {total.toLocaleString()}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground italic py-16">
          No data yet
        </div>
      ) : (
        <>
          {/* Donut chart with bottom legend chips */}
          <div className="h-60 px-2 pt-3 pb-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 4, right: 4, bottom: 30, left: 4 }}>
                <Pie
                  data={chartData}
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={1}
                  dataKey="value"
                  nameKey="name"
                  isAnimationActive={false}
                  label={false}
                  labelLine={false}
                >
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#fff" />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 11, padding: "6px 10px" }}
                  formatter={(v, n) => [v.toLocaleString(), n]}
                />
                <Legend
                  verticalAlign="bottom"
                  iconSize={10}
                  iconType="square"
                  wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  formatter={legendName}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Metric / Count / % table — visually separated from the legend
              above by an `mt-3` gap so legend chips never collide with
              the table header (esp. when the legend wraps to 3 lines). */}
          <div className="border-t-2 border-border mt-3 overflow-y-auto max-h-56" data-testid={`report-table-${report.key}`}>
            <table className="w-full text-xs">
              <thead className="bg-secondary sticky top-0 z-10 shadow-[0_1px_0_var(--border)]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">{report.metricLabel || "Metric"}</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">Count</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-secondary">%</th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 15).map((row, i) => (
                  <tr key={row.key} className="border-t border-border first:border-0" data-testid={`report-row-${report.key}-${i}`}>
                    <td className="px-3 py-1.5 truncate max-w-[160px]" title={row.key}>{row.key || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{row.count.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-muted-foreground">{(row.pct ?? 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// Stable formatter — keeps recharts Legend from rebuilding its DOM tree
// on every parent render (also silences react/no-unstable-nested-components).
function legendName(value) {
  return truncate(String(value || ""), 22);
}
